import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = import.meta.env.VITE_BACKEND_API_URL;

// Create an instance for authenticated requests (private)
const privateApi = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout
});

// Create an instance for unauthenticated requests (public)
const publicApi = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout
});

// Set Authorization header with token from cookies for private requests
privateApi.interceptors.request.use((config) => {
  const token = Cookies.get('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  // Handle request errors (e.g., network issues)
  return Promise.reject(error);
});

// Helper function to check if error is retryable
const isRetryableError = (error) => {
  // Don't retry if we've already exceeded max retries
  if (error.config && error.config._retryCount >= 3) {
    return false;
  }
  
  return (
    // Network errors
    error.code === 'ECONNRESET' ||
    error.code === 'ECONNABORTED' ||
    error.message === 'Network Error' ||
    // Timeout errors
    error.code === 'ECONNABORTED' ||
    error.message.includes('timeout') ||
    // Server errors (5xx)
    (error.response && error.response.status >= 500 && error.response.status < 600) ||
    // Connection refused
    error.message.includes('connect ECONNREFUSED') ||
    // No response from server
    !error.response
  );
};

// Response interceptor for handling errors and retrying
privateApi.interceptors.response.use(
  (response) => {
    // Reset retry count on successful response
    if (response.config) {
      response.config._retryCount = 0;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If no config, reject immediately
    if (!originalRequest) {
      return Promise.reject(error);
    }
    
    // Maximum number of retries
    const maxRetries = 3;
    // Current retry count
    const retryCount = originalRequest._retryCount || 0;
    
    // Handle 401 Unauthorized — session expired, clear cookies and redirect to login
    if (error.response && error.response.status === 401) {
      Cookies.remove('token', { path: '/' });
      Cookies.remove('user', { path: '/' });
      window.location.href = '/';
      return Promise.reject(error);
    }

    // Handle 429 Too Many Requests — rate limited, propagate so the form can show the message
    if (error.response && error.response.status === 429) {
      return Promise.reject(error);
    }

    // Handle 403 Forbidden — let the calling component show an error, do not redirect
    if (error.response && error.response.status === 403) {
      return Promise.reject(error);
    }
    
    // Check if we should retry the request
    if (isRetryableError(error) && retryCount < maxRetries) {
      // Increment retry count
      originalRequest._retryCount = retryCount + 1;
      
      // Calculate delay with exponential backoff (1s, 2s, 4s)
      const delay = Math.pow(2, retryCount) * 1000;
      
      // Log retry attempt
      console.log(`Retrying request (${retryCount + 1}/${maxRetries}) after ${delay}ms delay`, {
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response?.status
      });
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return privateApi(originalRequest);
    }

    // Log final failure
    console.log(`Request failed after ${retryCount} retries:`, {
      url: originalRequest.url,
      method: originalRequest.method,
      status: error.response?.status,
      message: error.message
    });
    
    // Return a rejected promise
    return Promise.reject(error);
  }
);

// Apply the same retry mechanism to public API
publicApi.interceptors.response.use(
  (response) => {
    // Reset retry count on successful response
    if (response.config) {
      response.config._retryCount = 0;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If no config, reject immediately
    if (!originalRequest) {
      return Promise.reject(error);
    }
    
    const maxRetries = 3;
    const retryCount = originalRequest._retryCount || 0;
    
    if (isRetryableError(error) && retryCount < maxRetries) {
      originalRequest._retryCount = retryCount + 1;
      const delay = Math.pow(2, retryCount) * 1000;
      
      console.log(`Retrying public request (${retryCount + 1}/${maxRetries}) after ${delay}ms delay`, {
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response?.status
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return publicApi(originalRequest);
    }
    
    // Log final failure
    console.log(`Public request failed after ${retryCount} retries:`, {
      url: originalRequest.url,
      method: originalRequest.method,
      status: error.response?.status,
      message: error.message
    });
    
    return Promise.reject(error);
  }
);

export { privateApi, publicApi };
